type Link = readonly [narrative: number, keyword: number];
interface GraphItem { title: string; displayTitle: string; url: string; meta: string; description: string; accent: string; keywords?: string[]; }

/** Keyword nodes are indexed after the narrative nodes. */
export const buildGraph = (items: readonly { keywords?: string[] }[]) => {
  const keywords = [...new Set(items.flatMap((item) => item.keywords ?? []))];
  const links: Link[] = items.flatMap((item, index) =>
    (item.keywords ?? []).map((keyword) => [index, items.length + keywords.indexOf(keyword)] as const));
  return { keywords, links };
};

/** A node in the simulation. Coordinates use the box's short side as 1; `w`/`h` is the node's label box in the same unit. */
export interface Body { x: number; y: number; vx: number; vy: number; w: number; h: number; }

const PAD = .09;
const GAP = .012;

/**
 * One tick of the force simulation: mutual repulsion, label-box collision, springs along links and a gentle
 * gravity toward the centre. `alpha` (0–1) scales every force so the system cools down; a `pinned` body
 * (being dragged) holds its position and the rest of the graph reacts to it. Constants tuned for ~25–100 nodes.
 */
export const stepGraph = (bodies: Body[], links: readonly Link[], width: number, height: number, alpha: number, pinned: number | null = null) => {
  const count = bodies.length;
  for (let i = 0; i < count; i++) {
    for (let j = i + 1; j < count; j++) {
      const a = bodies[i];
      const b = bodies[j];
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const distance = Math.max(Math.hypot(dx, dy), .01);
      const push = Math.min(.0003 / (distance * distance), .04) * alpha;
      a.vx -= dx / distance * push; a.vy -= dy / distance * push;
      b.vx += dx / distance * push; b.vy += dy / distance * push;
      // Labels are wide boxes, so overlap is resolved on boxes rather than on distance.
      const overlapX = (a.w + b.w) / 2 + GAP - Math.abs(dx);
      const overlapY = (a.h + b.h) / 2 + GAP - Math.abs(dy);
      if (overlapX > 0 && overlapY > 0) {
        const alongX = overlapX < overlapY;
        const shift = (alongX ? overlapX : overlapY) * .25;
        const sign = (alongX ? dx : dy) >= 0 ? 1 : -1;
        if (alongX) { a.vx -= shift * sign; b.vx += shift * sign; } else { a.vy -= shift * sign; b.vy += shift * sign; }
      }
    }
  }
  links.forEach(([i, j]) => {
    const a = bodies[i];
    const b = bodies[j];
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const distance = Math.max(Math.hypot(dx, dy), .01);
    const pull = (distance - .2) * .12 * alpha;
    a.vx += dx / distance * pull; a.vy += dy / distance * pull;
    b.vx -= dx / distance * pull; b.vy -= dy / distance * pull;
  });
  bodies.forEach((body, index) => {
    if (index === pinned) { body.vx = 0; body.vy = 0; return; }
    body.vx += (width / 2 - body.x) * .02 * alpha;
    body.vy += (height / 2 - body.y) * .02 * alpha;
    body.vx *= .6; body.vy *= .6;
    // Keep the whole label box inside the frame, not just its centre.
    const marginX = Math.max(PAD, body.w / 2 + GAP);
    const marginY = Math.max(PAD, body.h / 2 + GAP);
    body.x = Math.min(Math.max(body.x + body.vx, marginX), width - marginX);
    body.y = Math.min(Math.max(body.y + body.vy, marginY), height - marginY);
  });
};

/**
 * Deterministic initial layout inside a box whose short side is 1 and long side is `aspect` (or 1 / `aspect`),
 * so spacing stays consistent between landscape and portrait frames. Sizes are optional label boxes in that unit.
 */
export const layoutGraph = (nodeCount: number, links: readonly Link[], aspect: number, sizes: readonly { w: number; h: number }[] = [], ticks = 500): Body[] => {
  const width = Math.max(aspect, 1);
  const height = Math.max(1 / aspect, 1);
  // Golden-angle spiral start: spreads nodes evenly without favouring narratives or keywords.
  const bodies: Body[] = Array.from({ length: nodeCount }, (_, index) => {
    const angle = index * 2.39996;
    const radius = .42 * Math.sqrt((index + .5) / nodeCount);
    return { x: width / 2 + Math.cos(angle) * radius * width, y: height / 2 + Math.sin(angle) * radius * height, vx: 0, vy: 0, w: sizes[index]?.w ?? 0, h: sizes[index]?.h ?? 0 };
  });
  for (let tick = 0; tick < ticks; tick++) stepGraph(bodies, links, width, height, 1 - tick / ticks);
  // Stretch the settled layout so it fills the frame on both axes.
  const xs = bodies.map((body) => body.x);
  const ys = bodies.map((body) => body.y);
  const widest = Math.max(PAD, ...bodies.map((body) => body.w / 2 + GAP));
  const tallest = Math.max(PAD, ...bodies.map((body) => body.h / 2 + GAP));
  const fit = (value: number, min: number, max: number, size: number, margin: number) =>
    max - min < 1e-6 ? size / 2 : margin + (value - min) / (max - min) * (size - margin * 2);
  const [minX, maxX, minY, maxY] = [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)];
  bodies.forEach((body) => { body.x = fit(body.x, minX, maxX, width, widest); body.y = fit(body.y, minY, maxY, height, tallest); });
  // Collision-only relaxation (alpha 0 switches off every other force) to clear labels the stretch left touching.
  for (let tick = 0; tick < 150; tick++) stepGraph(bodies, links, width, height, 0);
  return bodies;
};

// Roughly how much frame area each node gets; the world grows past the frame (and becomes pannable) as the catalog grows.
const AREA_PER_NODE = 38_000;
// Phones grow the world sideways only (a horizontal swipe pans it; vertical swipes keep scrolling the page).
const AREA_PER_NODE_NARROW = 24_000;

export const setupNarrativeGraph = (root: HTMLElement, narratives: readonly GraphItem[]) => {
  const svg = root.querySelector<SVGSVGElement>('.ng-edges')!;
  const { keywords, links } = buildGraph(narratives);
  const count = narratives.length;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const canHover = window.matchMedia('(hover: hover)').matches;

  const world = document.createElement('div');
  world.className = 'ng-world';
  world.append(svg);
  const label = document.createElement('p');
  label.className = 'ng-label';
  label.setAttribute('aria-hidden', 'true');
  label.textContent = `SCNT / GRAFO · ${count} narrativas · ${keywords.length} temas`;
  const reset = document.createElement('button');
  reset.type = 'button';
  reset.className = 'ng-reset';
  reset.textContent = '↺ Reorganizar';
  reset.title = 'Reorganizar e mostrar todas as narrativas na tela';
  root.append(world, label, reset);

  const narrativeEls = narratives.map((item, index) => {
    const node = document.createElement('a');
    node.className = 'ng-node';
    node.href = item.url;
    node.target = '_blank';
    node.rel = 'noopener';
    node.draggable = false;
    node.style.setProperty('--accent', item.accent);
    node.style.setProperty('--i', String(index));
    node.setAttribute('aria-label', `${item.title} — ${item.description}`);
    node.innerHTML = `<span class="ng-orb" aria-hidden="true"></span>
      <span class="ng-card" aria-hidden="true">
        <span class="ng-meta">${item.meta}</span>
        <span class="ng-title">${item.displayTitle.replace(/<br>/g, ' ')}</span>
        <span class="ng-desc">${item.description}</span>
        <span class="ng-cta">Explorar narrativa <b>↗</b></span>
      </span>`;
    return node;
  });
  const keywordEls = keywords.map((keyword) => {
    const node = document.createElement('span');
    node.className = 'ng-key';
    node.textContent = keyword;
    return node;
  });
  const nodeEls: HTMLElement[] = [...narrativeEls, ...keywordEls];
  const lineEls = links.map(([a]) => {
    const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    line.style.setProperty('--accent', narratives[a].accent);
    return line;
  });
  svg.append(...lineEls);
  world.append(...keywordEls, ...narrativeEls);

  let bodies: Body[] = [];
  let unit = 1;
  let alpha = 0;
  const at = (index: number) => ({ x: (bodies[index]?.x ?? 0) * unit, y: (bodies[index]?.y ?? 0) * unit });
  let frame = { width: 0, height: 0 };
  let space = { width: 0, height: 0 };
  let pan = { x: 0, y: 0 };
  const glide = { vx: 0, vy: 0, frame: 0 };

  const applyPan = () => {
    // Keep at least a margin of the world in view; centre it when it is smaller than the frame.
    const clampAxis = (value: number, view: number, size: number) =>
      size <= view ? (view - size) / 2 : Math.min(Math.max(value, view - size), 0);
    const next = { x: clampAxis(pan.x, frame.width, space.width), y: clampAxis(pan.y, frame.height, space.height) };
    if (next.x !== pan.x) glide.vx = 0;
    if (next.y !== pan.y) glide.vy = 0;
    pan = next;
    root.classList.toggle('at-start', pan.x >= -1);
    root.classList.toggle('at-end', pan.x <= frame.width - space.width + 1);
    if (shown !== null) placeCard(shown);
    world.style.transform = `translate3d(${pan.x}px, ${pan.y}px, 0)`;
  };

  // Highlight a node, its direct links and (for narratives) siblings that share a keyword.
  let shown: number | null = null;
  const focusNode = (index: number | null) => {
    shown = index !== null && index < count ? index : null;
    root.classList.toggle('is-focus', index !== null);
    const lit = new Set<number>();
    const near = new Set<number>();
    if (index !== null) {
      lit.add(index);
      links.forEach(([a, b]) => { if (a === index) lit.add(b); if (b === index) lit.add(a); });
      if (index < count) links.forEach(([a, b]) => { if (lit.has(b) && a !== index) near.add(a); });
    }
    nodeEls.forEach((el, i) => {
      el.classList.toggle('is-lit', lit.has(i));
      el.classList.toggle('is-near', near.has(i));
    });
    narrativeEls.forEach((el, i) => el.classList.toggle('is-active', i === index));
    lineEls.forEach((line, i) => {
      const [a, b] = links[i];
      line.classList.toggle('is-lit', a === index || b === index);
      line.classList.toggle('is-near', index !== null && index < count && near.has(a) && lit.has(b));
    });
    if (index !== null && index < count) placeCard(index);
  };

  // Opens the card on the side away from the narrative's keywords, then keeps it inside the frame.
  const placeCard = (index: number) => {
    const node = narrativeEls[index];
    const card = node.querySelector<HTMLElement>('.ng-card')!;
    const bounds = root.getBoundingClientRect();
    const x = at(index).x + pan.x;
    const keywordXs = links.filter(([a]) => a === index).map(([, b]) => at(b).x + pan.x);
    const towardKeywords = keywordXs.reduce((sum, value) => sum + value, 0) / keywordXs.length - x;
    node.classList.toggle('is-flip', bounds.width - x < 340 || (towardKeywords > 0 && x > 340));
    card.style.setProperty('--dx', '0px');
    card.style.setProperty('--dy', '0px');
    const rect = card.getBoundingClientRect();
    const margin = 12;
    const dx = Math.max(bounds.left + margin - rect.left, Math.min(0, bounds.right - margin - rect.right));
    const dy = Math.max(bounds.top + margin - rect.top, Math.min(0, bounds.bottom - margin - rect.bottom));
    card.style.setProperty('--dx', `${dx}px`);
    card.style.setProperty('--dy', `${dy}px`);
  };

  // Pointer: drag a node to move it, drag the background to pan. A drag never counts as a click.
  let drag: { id: number; index: number | null; startX: number; startY: number; lastX: number; lastY: number; lastT: number; moved: boolean } | null = null;
  let suppressClick = false;

  root.addEventListener('pointerdown', (event) => {
    // The card is a plain link: never start a drag from it.
    if (event.button !== 0 || (event.target as Element).closest('.ng-reset, .ng-card')) return;
    const nodeEl = (event.target as Element).closest<HTMLElement>('.ng-node, .ng-key');
    // Touch always pans (a tap still pins); moving nodes is a mouse/pen gesture.
    const index = nodeEl && event.pointerType !== 'touch' ? nodeEls.indexOf(nodeEl) : null;
    cancelAnimationFrame(glide.frame);
    glide.vx = 0; glide.vy = 0;
    drag = { id: event.pointerId, index, startX: event.clientX, startY: event.clientY, lastX: event.clientX, lastY: event.clientY, lastT: event.timeStamp, moved: false };
  });
  root.addEventListener('pointermove', (event) => {
    if (!drag || event.pointerId !== drag.id) return;
    const dx = event.clientX - drag.lastX;
    const dy = event.clientY - drag.lastY;
    const dt = Math.max(event.timeStamp - drag.lastT, 1);
    drag.lastX = event.clientX;
    drag.lastY = event.clientY;
    drag.lastT = event.timeStamp;
    if (!drag.moved && Math.hypot(event.clientX - drag.startX, event.clientY - drag.startY) < 5) return;
    if (!drag.moved) {
      drag.moved = true;
      // Capture only once it is a real drag, so a plain click still reaches the link underneath.
      try { root.setPointerCapture(event.pointerId); } catch { /* pointer already released */ }
      if (drag.index === null) root.classList.add('is-panning');
      else nodeEls[drag.index].classList.add('is-dragging');
    }
    if (drag.index === null) {
      pan = { x: pan.x + dx, y: pan.y + dy };
      // Smoothed velocity (px per frame) for the glide after release.
      glide.vx = glide.vx * .6 + dx / dt * 16 * .4;
      glide.vy = glide.vy * .6 + dy / dt * 16 * .4;
      applyPan();
    } else {
      // The dragged body is pinned under the pointer; the simulation pulls its neighbours along.
      const body = bodies[drag.index];
      body.x = Math.min(Math.max(body.x + dx / unit, 0), space.width / unit);
      body.y = Math.min(Math.max(body.y + dy / unit, 0), space.height / unit);
      heat(.3);
    }
  });
  const endDrag = (event: PointerEvent) => {
    if (!drag || event.pointerId !== drag.id) return;
    if (drag.moved) {
      // Swallow the click a mouse fires right after a drag; touch fires none, so clear it on the next task.
      suppressClick = true;
      setTimeout(() => { suppressClick = false; }, 0);
    }
    if (drag.moved && drag.index === null && event.type === 'pointerup' && Math.hypot(glide.vx, glide.vy) > .5) {
      // Inertia: keep gliding and ease out, like native scrolling.
      const step = () => {
        glide.vx *= .94; glide.vy *= .94;
        pan = { x: pan.x + glide.vx, y: pan.y + glide.vy };
        applyPan();
        if (Math.hypot(glide.vx, glide.vy) > .2) glide.frame = requestAnimationFrame(step);
      };
      glide.frame = requestAnimationFrame(step);
    }
    if (drag.index !== null) nodeEls[drag.index].classList.remove('is-dragging');
    root.classList.remove('is-panning');
    drag = null;
  };
  root.addEventListener('pointerup', endDrag);
  root.addEventListener('pointercancel', endDrag);

  // A click pins a node's highlight and card; hover and keyboard focus preview others and fall back to the pin.
  let pinned: number | null = null;
  const release = () => { if (!drag?.moved) focusNode(pinned); };
  const pin = (index: number | null) => { pinned = index; focusNode(index); };

  nodeEls.forEach((el, index) => {
    if (canHover) {
      el.addEventListener('pointerenter', () => { if (!drag?.moved) focusNode(index); });
      el.addEventListener('pointerleave', release);
    }
    el.addEventListener('focus', () => {
      // Bring keyboard-focused nodes into view.
      if (el.matches(':focus-visible') && bodies[index]) {
        const point = at(index);
        const x = point.x + pan.x;
        const y = point.y + pan.y;
        if (x < 60 || x > frame.width - 60 || y < 60 || y > frame.height - 60) {
          pan = { x: frame.width / 2 - point.x, y: frame.height / 2 - point.y };
          applyPan();
        }
      }
      focusNode(index);
    });
    el.addEventListener('blur', release);
    // First click pins; clicking the pinned narrative (or its card) follows the link.
    el.addEventListener('click', (event) => {
      if (suppressClick) { event.preventDefault(); suppressClick = false; return; }
      if (index < count && (pinned === index || (event.target as Element).closest('.ng-card'))) return;
      event.preventDefault();
      pin(index);
    });
  });
  // Clicking anywhere else — the empty graph or the rest of the page — clears the pin. A pan drag does not.
  document.addEventListener('click', (event) => {
    if ((event.target as Element).closest?.('.ng-node, .ng-key')) return;
    if (suppressClick) { suppressClick = false; return; }
    if (pinned !== null) pin(null);
  });

  const draw = (time: number) => {
    const drift = reduceMotion ? 0 : 7;
    const dragged = drag?.moved ? drag.index : null;
    const points = bodies.map((body, i) => i === dragged ? at(i) : ({
      x: body.x * unit + Math.sin(time * .00035 + i * 1.7) * drift,
      y: body.y * unit + Math.cos(time * .0003 + i * 2.3) * drift,
    }));
    nodeEls.forEach((el, i) => { el.style.transform = `translate3d(${points[i].x}px, ${points[i].y}px, 0) translate(-50%, -50%)`; });
    lineEls.forEach((line, i) => {
      const [a, b] = links[i];
      line.setAttribute('x1', points[a].x.toFixed(1)); line.setAttribute('y1', points[a].y.toFixed(1));
      line.setAttribute('x2', points[b].x.toFixed(1)); line.setAttribute('y2', points[b].y.toFixed(1));
    });
  };

  // `fit` lays the whole graph out inside the visible frame (no panning needed); otherwise the world grows with the catalog.
  const layout = (fit = false) => {
    cancelAnimationFrame(glide.frame);
    glide.vx = 0; glide.vy = 0;
    if (fit) {
      space = { width: frame.width, height: frame.height };
    } else if (frame.width < 761) {
      space = { width: Math.max(frame.width, nodeEls.length * AREA_PER_NODE_NARROW / frame.height), height: frame.height };
    } else {
      const scale = Math.max(1, Math.sqrt(nodeEls.length * AREA_PER_NODE / (frame.width * frame.height)));
      space = { width: frame.width * scale, height: frame.height * scale };
    }
    root.classList.toggle('is-pannable', space.width > frame.width + 1 || space.height > frame.height + 1);
    world.style.width = `${space.width}px`;
    world.style.height = `${space.height}px`;
    svg.setAttribute('viewBox', `0 0 ${space.width} ${space.height}`);
    unit = Math.min(space.width, space.height);
    const sizes = nodeEls.map((el) => ({ w: el.offsetWidth / unit, h: el.offsetHeight / unit }));
    bodies = layoutGraph(nodeEls.length, links, space.width / space.height, sizes);
    alpha = 0;
    pan = frame.width < 761 ? { x: 0, y: 0 } : { x: (frame.width - space.width) / 2, y: (frame.height - space.height) / 2 };
    applyPan();
    draw(performance.now());
  };

  const measure = () => {
    const width = root.clientWidth;
    const height = root.clientHeight;
    if (!width || !height || (width === frame.width && height === frame.height)) return;
    frame = { width, height };
    layout();
  };
  new ResizeObserver(measure).observe(root);
  reset.addEventListener('click', () => { pin(null); layout(true); });

  // Animation: the simulation runs while it is warm; the ambient drift keeps the loop alive otherwise.
  let visible = false;
  let rafId = 0;
  const loop = (time: number) => {
    rafId = 0;
    if (alpha > .002) {
      const pinned = drag?.moved ? drag.index : null;
      stepGraph(bodies, links, space.width / unit, space.height / unit, alpha, pinned);
      alpha *= pinned === null ? .97 : 1;
    }
    draw(time);
    if (visible && (!reduceMotion || alpha > .002)) rafId = requestAnimationFrame(loop);
  };
  const start = () => { if (visible && !rafId) rafId = requestAnimationFrame(loop); };
  function heat(value: number) { alpha = Math.max(alpha, value); start(); }
  new IntersectionObserver(([entry]) => {
    visible = entry.isIntersecting;
    if (visible && !reduceMotion) start();
  }).observe(root);
};
